import { parseUTCDate } from '@root/src/utils/formatters/dates';
import {
  DedicatedForumBack,
  DedicatedForumFront,
  DedicatedForumFileBack,
  DedicatedForumFileFront,
  DedicatedForumInvitedUserBack,
  DedicatedForumInvitedUserFront,
  DedicatedForumParticipantBack,
  DedicatedForumParticipantFront,
  DedicatedForumParticipantMemberBack,
  DedicatedForumParticipantMemberFront,
  DedicatedForumParticipantUserBack,
  DedicatedForumParticipantUserFront,
} from './types';
import { DedicatedForumMessageBack, DedicatedForumMessageFront } from './Message/type';

/**
 * A bare id (accept / role answers do not populate the user) keeps the id with
 * empty names. A missing user yields an empty id rather than throwing.
 */
const participantUserFromBack = (
  user: DedicatedForumParticipantUserBack | string | undefined,
): DedicatedForumParticipantUserFront =>
  typeof user === 'object' && user !== null
    ? { id: user._id, firstName: user.firstName || '', lastName: user.lastName || '' }
    : { id: user ?? '', firstName: '', lastName: '' };

/** Callers leave the member out when the backend answers `member: null` (a user without a member). */
const participantMemberFromBack = ({
  _id,
  name,
  country,
}: DedicatedForumParticipantMemberBack): DedicatedForumParticipantMemberFront => ({ id: _id, name, country });

export const participantFromBack = ({
  _id,
  forum,
  user,
  member,
  isAdmin,
  joinedAt,
  accessRequestStatus,
  createdAt,
  updatedAt,
}: DedicatedForumParticipantBack): DedicatedForumParticipantFront => ({
  id: _id,
  forum,
  user: participantUserFromBack(user),
  ...(member && { member: participantMemberFromBack(member) }),
  isAdmin,
  joinedAt: joinedAt ? parseUTCDate(joinedAt) : new Date(NaN),
  accessRequestStatus,
  createdAt: createdAt ? parseUTCDate(createdAt) : new Date(NaN),
  updatedAt: updatedAt ? parseUTCDate(updatedAt) : new Date(NaN),
});

export const invitedUserFromBack = ({
  user,
  member,
  invitedAt,
  invitedBy,
}: DedicatedForumInvitedUserBack): DedicatedForumInvitedUserFront => ({
  user: participantUserFromBack(user),
  ...(member && { member: participantMemberFromBack(member) }),
  invitedAt: invitedAt ? parseUTCDate(invitedAt) : new Date(NaN),
  invitedBy: participantUserFromBack(invitedBy),
});

export const messageFromBack = ({
  _id,
  forum,
  author,
  content,
  parentMessageId,
  sstBlockId,
  deleted,
  readBy,
  edited,
  files,
  replyCount,
  lastReplyTime,
  createdAt,
  updatedAt,
}: DedicatedForumMessageBack): DedicatedForumMessageFront => ({
  id: _id,
  forum,
  author: author
    ? {
        id: author._id,
        member: author.member
          ? {
              id: author.member._id,
              name: author.member.name || '',
              state: author.member.state,
            }
          : undefined,
        firstName: author.firstName || '',
        lastName: author.lastName || '',
        email: author.email || '',
      }
    : {
        id: '',
        firstName: '',
        lastName: '',
        email: '',
      },
  content: content || '',
  ...(parentMessageId && { parentMessageId }),
  ...(sstBlockId && { sstBlockId }),
  deleted,
  readBy: readBy
    ? new Map(Object.entries(readBy).map(([key, value]) => [key, value ? parseUTCDate(value) : new Date(NaN)]))
    : new Map(),
  ...(edited !== undefined && { edited }),
  ...(files && files.length > 0 && { files: files.map(fileFromBack) }),
  ...(replyCount !== undefined && { replyCount }),
  ...(lastReplyTime && { lastReplyTime: parseUTCDate(lastReplyTime) }),
  createdAt: createdAt ? parseUTCDate(createdAt) : new Date(NaN),
  updatedAt: updatedAt ? parseUTCDate(updatedAt) : new Date(NaN),
});

export const fileFromBack = ({
  _id,
  forum,
  message,
  file,
  uploadedBy,
  filename,
  mimeType,
  fileSize,
  createdAt,
  updatedAt,
}: DedicatedForumFileBack): DedicatedForumFileFront => ({
  id: _id,
  forum,
  ...(message && {
    message: {
      id: message._id,
      content: message.content || '',
      createdAt: message.createdAt ? parseUTCDate(message.createdAt) : new Date(NaN),
    },
  }),
  file,
  uploadedBy: uploadedBy
    ? {
        id: uploadedBy._id,
        firstName: uploadedBy.firstName || '',
        lastName: uploadedBy.lastName || '',
        email: uploadedBy.email || '',
        ...(uploadedBy.member && {
          member: {
            id: uploadedBy.member._id,
            name: uploadedBy.member.name || '',
            ...(uploadedBy.member.state && { state: uploadedBy.member.state }),
          },
        }),
      }
    : {
        id: '',
        firstName: '',
        lastName: '',
        email: '',
      },
  filename,
  mimeType,
  fileSize,
  createdAt: createdAt ? parseUTCDate(createdAt) : new Date(NaN),
  updatedAt: updatedAt ? parseUTCDate(updatedAt) : new Date(NaN),
});

export const dedicatedForumFromBack = ({
  _id,
  topic,
  description,
  category,
  picture,
  status,
  isArchived,
  showHistoryForNewcomers,
  allowFileSharing,
  sstBlocks,
  participantCount,
  messageCount,
  userRequestStatus,
  isParticipant,
  isCreator,
  createdBy,
  createdAt,
  updatedAt,
}: DedicatedForumBack): DedicatedForumFront => ({
  id: _id,
  topic,
  description,
  category,
  picture, // Include picture field (can be undefined)
  status,
  isArchived,
  showHistoryForNewcomers,
  allowFileSharing,
  sstBlocks,
  participantCount,
  messageCount,
  ...(userRequestStatus && { userRequestStatus }),
  ...(isParticipant !== undefined && { isParticipant }),
  ...(isCreator !== undefined && { isCreator }),
  createdBy,
  createdAt: parseUTCDate(createdAt),
  updatedAt: parseUTCDate(updatedAt),
});
